import React, { useEffect, useRef } from 'react';
import { Resource } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { useMotion } from '../contexts/MotionContext';
import { highlightText } from '../utils/highlightText';
import { normalizeCategoryL1, normalizeCategoryL2, type CategoryL1, type CategoryL2 } from '../server/categoryTaxonomy';
import { useImageWithProxyFallback } from '../hooks/useImageWithProxyFallback';
import { isIconUrl } from '../utils/mediaProxy';
import { formatRelativeTime } from '../utils/relativeTime';
import { formatCount } from '../utils/formatCount';
import { trackEvent } from '../lib/analytics';
import { recordResourceView } from '../lib/resourceView';
import VisibilityBadge from './VisibilityBadge';

interface ResourceCardProps {
  resource: Resource;
  index: number;
  searchQuery?: string;
  activeTags?: string[];
  onTagClick?: (tag: string) => void;
  isFavorited?: boolean;
  onToggleFavorite?: (resourceId: string, next: boolean) => void;
  isFavoriteActionPending?: boolean;
  onOpenRepoInsight?: (resource: Resource) => void;
}

const CATEGORY_L1_LABELS: Record<CategoryL1, { zh: string; en: string }> = {
  models: { zh: '模型', en: 'Models' },
  apis: { zh: '接口', en: 'APIs' },
  agents: { zh: '智能体', en: 'Agents' },
  frameworks: { zh: '框架', en: 'Frameworks' },
  tools: { zh: '工具', en: 'Tools' },
  apps: { zh: '应用', en: 'Apps' },
  data: { zh: '数据', en: 'Data' },
  tutorials: { zh: '教程', en: 'Tutorials' },
  community: { zh: '社区', en: 'Community' },
  pending: { zh: '待定', en: 'Pending' },
};

const CATEGORY_L2_LABELS: Record<CategoryL2, { zh: string; en: string }> = {
  development: { zh: '开发', en: 'Development' },
  office: { zh: '办公', en: 'Office' },
  content_creation: { zh: '内容创作', en: 'Content Creation' },
  data_analysis: { zh: '数据分析', en: 'Data Analysis' },
  design: { zh: '设计', en: 'Design' },
  marketing: { zh: '营销', en: 'Marketing' },
  security: { zh: '安全', en: 'Security' },
  education: { zh: '教育', en: 'Education' },
};

const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const ResourceCard: React.FC<ResourceCardProps> = ({
  resource,
  index,
  searchQuery = '',
  activeTags = [],
  onTagClick,
  isFavorited = false,
  onToggleFavorite,
  isFavoriteActionPending = false,
  onOpenRepoInsight,
}) => {
  const { language, t } = useLanguage();
  const { isFull } = useMotion();
  const cardRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);

  const safeUrl = isValidUrl(resource.url) ? resource.url : '#';
  const isGitHubResource = /^https?:\/\/(www\.)?github\.com\//i.test(safeUrl);
  const title = (language === 'zh' && resource.title_zh) ? resource.title_zh : resource.title;
  const description = (language === 'zh' && resource.description_zh) ? resource.description_zh : resource.description;
  const opensInNewTabLabel = language === 'zh' ? '（新标签页打开）' : '(Opens in new tab)';

  // Staggered animation delay based on index
  const animationDelay = `${index * 100}ms`;

  const hasIconUrl = isIconUrl(resource.icon);
  const { src: iconSrc, mode: iconMode, onError: handleIconError, onLoad: handleIconLoad } = useImageWithProxyFallback(
    hasIconUrl ? resource.icon : undefined,
  );
  const relativeTime = formatRelativeTime(resource.created_at, language);
  const viewCount = formatCount(resource.view_count, language);
  const favoriteCount = formatCount(resource.favorite_count, language);
  const categoryL1 = normalizeCategoryL1(resource.category_l1 || resource.category);
  const categoryL2 = normalizeCategoryL2(resource.category_l2 || 'development');

  // Fallback icon generator (first letter of title)
  const FallbackIcon = () => (
    <span className="text-xl font-bold text-slate-700 dark:text-white/80">{title.charAt(0)}</span>
  );

  // Detect monochrome icons that need inversion in dark mode
  const isMonochrome = (() => {
    const icon = resource.icon;
    if (!icon || typeof icon !== 'string') return false;
    // Check for black color in iconify URL
    if (icon.includes('color=%23000000') || icon.includes('color=black')) return true;
    // Check for GitHub's own assets (dark octocat icons)
    if (icon.includes('github.githubassets.com') || icon.includes('githubusercontent.com')) return true;
    // Check for known monochrome simple-icons
    const monochromeIcons = ['github', 'x', 'devdotto', 'openai', 'anthropic', 'nextdotjs', 'vercel'];
    return monochromeIcons.some(name => icon.includes(`simple-icons:${name}`));
  })();

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const scheduleSpotlightUpdate = (x: number, y: number) => {
    if (!isFull) return;
    lastPosRef.current = { x, y };
    if (rafRef.current) return;

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const el = cardRef.current;
      const pos = lastPosRef.current;
      if (!el || !pos) return;
      el.style.setProperty('--mouse-x', `${pos.x}px`);
      el.style.setProperty('--mouse-y', `${pos.y}px`);
    });
  };

  return (
    <div
      style={{ animationDelay }}
      className="resource-card group relative block h-full cursor-pointer animate-fade-in-up opacity-0 fill-mode-forwards"
      onMouseMove={(e) => {
        if (!isFull) return;
        const el = cardRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        scheduleSpotlightUpdate(e.clientX - rect.left, e.clientY - rect.top);
      }}
    >
      <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-secondary rounded-2xl blur opacity-0 group-hover:opacity-30 transition duration-500 group-hover:duration-200 pointer-events-none"></div>

      <div
        ref={cardRef}
        className="glass-panel spotlight-card relative z-30 h-full rounded-2xl p-6 transition-all duration-300 flex flex-col group-hover:-translate-y-1 group-hover:shadow-2xl group-hover:shadow-primary/10 pointer-events-none"
      >
        {resource.featured && isFull && <div className="scan-line" />}
        <div className="flex justify-between items-start mb-5">
          {/*
             Icon Container
             Light Mode: bg-slate-100 (Light Gray)
             Dark Mode: bg-slate-900 (Dark)
          */}
          <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-white/10 flex items-center justify-center text-2xl group-hover:scale-110 group-hover:bg-white dark:group-hover:bg-slate-800 transition-all duration-300 shadow-sm overflow-hidden relative backdrop-blur-md">

            {hasIconUrl && iconSrc && iconMode !== 'failed' ? (
              <img
                src={iconSrc}
                alt={title}
                loading="lazy"
                decoding="async"
                onError={handleIconError}
                onLoad={handleIconLoad}
                // isMonochrome logic: If it's a black icon, invert it in dark mode to make it white.
                // Colored icons remain untouched.
                className={`w-9 h-9 object-contain opacity-90 group-hover:opacity-100 transition-all rounded-md relative z-10 ${isMonochrome ? 'dark:invert' : ''}`}
              />
            ) : (
              !hasIconUrl && resource.icon ? <span className="text-2xl filter drop-shadow-md relative z-10">{resource.icon}</span> : <FallbackIcon />
            )}
          </div>
          <div className="flex items-center gap-2">
            {isGitHubResource && onOpenRepoInsight && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onOpenRepoInsight(resource);
                }}
                className="pointer-events-auto px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold bg-primary/10 text-primary rounded-full border border-primary/20 hover:bg-primary/20 transition-colors"
              >
                {language === 'zh' ? 'AI速读' : 'AI Brief'}
              </button>
            )}
            {resource.visibility && resource.visibility !== 'public' && (
              <VisibilityBadge
                visibility={resource.visibility}
                teamName={resource.team_name}
              />
            )}
            {resource.featured && (
              <span className="px-3 py-1 text-[10px] uppercase tracking-wider font-bold bg-amber-500/10 text-amber-500 rounded-full border border-amber-500/20 shadow-[0_0_10px_rgba(245,158,11,0.2)]">
                {t.featured}
              </span>
            )}
          </div>
        </div>
        
        <h3 className="text-xl font-bold font-display text-slate-900 dark:text-white mb-2 group-hover:text-primary transition-colors flex items-start gap-2">
          <span className="min-w-0 flex-1 break-words">
            {searchQuery ? highlightText(title, searchQuery) : title}
          </span>
          <svg className="w-4 h-4 shrink-0 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </h3>

        <p className="text-slate-500 dark:text-slate-400 text-sm mb-4 line-clamp-3 leading-relaxed group-hover:text-slate-700 dark:group-hover:text-slate-300 transition-colors">
          {searchQuery ? highlightText(description, searchQuery) : description}
        </p>

        <div className="mt-auto">
          <div className="flex flex-wrap gap-2 mb-2">
            <span className="px-2.5 py-1 text-[11px] rounded-lg bg-primary/10 text-primary border border-primary/20">
              {language === 'zh' ? CATEGORY_L1_LABELS[categoryL1].zh : CATEGORY_L1_LABELS[categoryL1].en}
            </span>
            <span className="px-2.5 py-1 text-[11px] rounded-lg bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-white/10">
              {language === 'zh' ? CATEGORY_L2_LABELS[categoryL2].zh : CATEGORY_L2_LABELS[categoryL2].en}
            </span>
          </div>

          <div className="flex flex-wrap gap-2 content-start">
            {resource.tags.slice(0, 5).map((tag, index) => {
              const isActive = activeTags.some((value) => value.toLowerCase() === tag.toLowerCase());
              return (
                <button
                  key={index}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onTagClick?.(tag);
                  }}
                  className={`px-2.5 py-1 text-xs font-medium rounded-lg border font-mono tracking-wider transition-colors pointer-events-auto ${
                    isActive
                      ? 'bg-primary/10 text-primary border-primary/30'
                      : 'bg-slate-100 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-white/5 hover:border-primary/30 hover:text-primary'
                  }`}
                >
                  #{tag}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400 mt-4 py-2 border-t border-slate-100 dark:border-white/5">
            <div className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{relativeTime || '-'}</span>
            </div>

            <div className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span>{viewCount}</span>
            </div>

            {onToggleFavorite ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleFavorite(resource.id, !isFavorited);
                }}
                disabled={isFavoriteActionPending}
                aria-label={
                  language === 'zh'
                    ? (isFavorited ? '取消收藏' : '收藏')
                    : (isFavorited ? 'Unfavorite' : 'Favorite')
                }
                className={
                  'pointer-events-auto flex items-center gap-1.5 ml-auto transition-all hover:text-primary hover:scale-110 active:scale-95 disabled:opacity-50 disabled:hover:scale-100 ' +
                  (isFavorited ? 'text-primary' : '')
                }
              >
                <svg
                  className="w-3.5 h-3.5"
                  viewBox="0 0 24 24"
                  fill={isFavorited ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <span>{favoriteCount}</span>
              </button>
            ) : (
              <div className="flex items-center gap-1.5 ml-auto">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <span>{favoriteCount}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <a
        href={safeUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${title} ${opensInNewTabLabel}`}
        onClick={() => {
          trackEvent('resource_click', {
            resource_id: resource.id,
            resource_title: resource.title,
            resource_url: resource.url,
          });
          void recordResourceView(resource.id).catch(() => {});
        }}
        className="absolute inset-0 z-20 cursor-pointer"
      />
    </div>
  );
};

export default React.memo(ResourceCard);
